import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot, Router } from '@angular/router';
import { AuthService } from '../../../shared/src/lib/services/auth.service';
import { CREDENTIAL_CHANGE_ROUTE, isPasswordExpired } from '../../../shared/src/lib/services/api.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(private readonly authService: AuthService, private readonly router: Router) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    const requiredPermission = route.data['permission']; // Permission required by the route
    if (!this.authService.isAuthenticated()) {
      // Navigate to login page if user is not authenticated
      this.router.navigate(['authentication/login']);
      return false;
    }

    if (this.redirectedToPasswordChange(state)) {
      return false;
    }

    if (route.data['superAdminOnly'] === true && this.authService.getUserRole() !== 'super_admin') {
      this.router.navigate(['authentication/access-denied']);
      return false;
    }

    if (requiredPermission) {
      const sadminBypass = route.data['sadminBypass'] === true;
      const isSuperAdmin = this.authService.getUserRole() === 'super_admin';
      if (!(sadminBypass && isSuperAdmin) && !this.authService.hasPermission(requiredPermission)) {
        this.router.navigate(['authentication/access-denied']);
        return false;
      }
    }

    return true; // User is allowed to access the route
  }

  // Re-checked on every navigation inside the app, not just on entering it.
  // Permissions stay with each route's own canActivate.
  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    return !(this.authService.isAuthenticated() && this.redirectedToPasswordChange(state));
  }

  // An expired password may only be changed: keep the user on that page.
  private redirectedToPasswordChange(state: RouterStateSnapshot): boolean {
    if (!isPasswordExpired() || (state.url ?? '').startsWith(CREDENTIAL_CHANGE_ROUTE)) {
      return false;
    }
    this.router.navigate([CREDENTIAL_CHANGE_ROUTE]);
    return true;
  }
}

/**
 * Modern Angular functional route guard wrapper for AuthGuard.
 */
export const authGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => inject(AuthGuard).canActivate(route, state);

